import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../styles/landing.css';

const Privacy = () => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const nav = document.querySelector('.landing-nav');
        const handleScroll = () => {
            if (window.scrollY > 50) {
                nav?.classList.add('scrolled');
            } else {
                nav?.classList.remove('scrolled');
            }
        };
        window.addEventListener('scroll', handleScroll);

        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in-view');
                    obs.unobserve(entry.target);
                }
            });
        }, observerOptions);

        document.querySelectorAll('.scroll-animate').forEach((element) => {
            observer.observe(element);
        });

        window.scrollTo(0, 0);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    return (
        <div className="landing-body" style={{ background: '#fff' }}>
            <style>
                {`
                    .legal-page {
                        background: #fff;
                        color: #111827;
                        padding: 120px 20px 80px;
                        line-height: 1.6;
                    }

                    .legal-container {
                        max-width: 800px;
                        margin: 0 auto;
                    }

                    .legal-container h1 {
                        font-size: 2.5rem;
                        font-weight: 800;
                        margin-bottom: 40px;
                        color: #2A3F54;
                    }

                    .legal-container h2 {
                        font-size: 1.5rem;
                        font-weight: 700;
                        margin: 40px 0 20px;
                        color: #2A3F54;
                    }

                    .legal-container p {
                        margin-bottom: 20px;
                        color: #4b5563;
                    }

                    .legal-container ul {
                        margin-bottom: 20px;
                        padding-left: 20px;
                        color: #4b5563;
                    }

                    .legal-container li {
                        margin-bottom: 10px;
                    }

                    .last-updated {
                        color: #9ca3af;
                        font-size: 0.9rem;
                        margin-bottom: 40px;
                    }
                `}
            </style>

            {/* Navigation */}
            <nav className={`landing-nav scrolled${menuOpen ? ' nav-open' : ''}`}>
                <div className="logo">
                    <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fa-solid fa-layer-group logo-icon" style={{ color: 'var(--lp-primary-orange)' }}></i>
                        <span style={{ color: 'var(--lp-primary-orange)' }}>envion</span>
                    </Link>
                </div>
                <button className="hamburger-btn" onClick={() => setMenuOpen(!menuOpen)}>
                    <i className={menuOpen ? 'fa-solid fa-xmark' : 'fa-solid fa-bars'}></i>
                </button>
                <div className="nav-links">
                    <Link to="/solutions" className="nav-link">Solutions</Link>
                    <Link to="/about" className="nav-link">About Us</Link>
                    <Link to="/#how-it-works" className="nav-link">How it Works</Link>
                    <Link to="/contact" className="nav-link">Contact</Link>
                </div>
                <div className="auth-buttons">
                    <button onClick={() => navigate('/login')} className="btn-lp btn-lp-secondary w-full sm:w-auto text-center" style={{ cursor: 'pointer' }}>Log In</button>
                    <Link to="/contact" className="btn-lp btn-lp-primary">Get a Quote</Link>
                </div>
            </nav>

            <main className="legal-page">
                <div className="legal-container scroll-animate animate-fade-in">
                    <h1>Privacy Policy</h1>
                    <p className="last-updated">Last updated: March 2025</p>

                    <h2>1. Introduction</h2>
                    <p>Envion Ltd ("Envion", "we", "our", "us") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, store, and protect personal data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>

                    <p>This policy applies to all personal data we collect when you visit our website at envion.org.uk, interact with us through enquiries or demo bookings, and use the Envion platform as an organisation or as an employee being onboarded through the platform.</p>

                    <p>Envion Ltd is registered with the Information Commissioner's Office (ICO). Registration number: [Pending — to be inserted upon confirmation].</p>

                    <h2>2. Who We Are</h2>
                    <p>Envion Ltd is a company registered in England and Wales. We provide an AI-powered employee onboarding and preboarding platform delivered as a software-as-a-service (SaaS) solution.</p>

                    <p>For the purposes of data protection law, Envion acts as a Data Controller when we collect personal data directly from website visitors, prospective customers, and individuals who contact us. Envion acts as a Data Processor when we process personal data on behalf of our customers through the platform. In this capacity, the customer organisation is the Data Controller and Envion processes data only on their instructions, as set out in our Terms and Conditions.</p>

                    <p><strong>Data Protection Contact:</strong><br />Email: <a href="mailto:enquiries@envion.org.uk">enquiries@envion.org.uk</a></p>

                    <h2>3. Information We Collect</h2>
                    <h3>3.1 Website Visitors</h3>
                    <p>When you visit our website or interact with us, we may collect the following:</p>
                    <ul>
                        <li><strong>Information you provide to us:</strong> Name, email address, phone number, company name, job title, and any information you include in enquiry forms or when booking a demo through Microsoft Bookings.</li>
                        <li><strong>Technical information collected automatically:</strong> IP address, browser type and version, operating system, device information, time zone setting, pages visited, time spent on pages, and referral source. This information is collected through cookies and Google Analytics.</li>
                    </ul>

                    <h3>3.2 Customer Organisations</h3>
                    <p>When an organisation subscribes to the Envion platform, we collect information about the organisation and its administrators, including company name, billing contact details, administrator names and email addresses, and payment information processed securely through Stripe.</p>

                    <h3>3.3 Employees Being Onboarded (Platform Data)</h3>
                    <p>When our customers use the platform to onboard employees, the following categories of personal data may be processed through the platform:</p>
                    <p>Employee names and contact details (email, phone number, address), job title, department, and role information, start date and employment details, right to work documentation, onboarding task completion and progress data, documents uploaded by the employee or their employer, and any other information the customer chooses to include in their onboarding journeys.</p>
                    <p>This data is provided by and controlled by the customer organisation. Envion processes this data solely on behalf of the customer in accordance with our Terms and Conditions and the Data Processing Agreement contained within them.</p>

                    <h2>4. How We Use Your Information</h2>
                    <h3>4.1 Website Visitors and Prospective Customers</h3>
                    <p>We use your information to respond to enquiries and demo requests, schedule and manage demo bookings through Microsoft Bookings, send follow-up communications after demos or enquiries, improve our website and understand how visitors use it through analytics, process payments securely through Stripe, comply with legal obligations, and detect and prevent fraudulent activity.</p>

                    <h3>4.2 Platform Data (Employee Onboarding)</h3>
                    <p>We process personal data through the platform solely to provide the onboarding and preboarding service to our customers. This includes hosting and storing data securely, enabling automated onboarding journeys and intelligent pathways, providing AI-powered personalisation features, generating progress tracking and reporting for customer administrators, and sending notifications and communications as configured by the customer.</p>
                    <p>We do not use platform data for our own marketing purposes, sell platform data to third parties, or access platform data except as necessary to provide the service or as instructed by the customer.</p>

                    <h2>5. Legal Basis for Processing</h2>
                    <p>We process personal data based on the following legal grounds:</p>
                    <ul>
                        <li><strong>Contract:</strong> Where processing is necessary to provide our services, manage subscriptions, or fulfil a contract with you.</li>
                        <li><strong>Consent:</strong> Where you have given explicit consent, such as opting in to marketing communications or booking a demo.</li>
                        <li><strong>Legitimate Interests:</strong> Where processing is in our legitimate business interests and does not override your rights, such as improving our website and services, ensuring security, and communicating with prospective customers.</li>
                        <li><strong>Legal Obligation:</strong> Where processing is necessary to comply with a legal requirement.</li>
                    </ul>
                    <p>For platform data processed on behalf of customers, the customer (as Data Controller) determines the legal basis for processing their employees' data. Envion processes this data on the basis of contractual obligation to the customer.</p>

                    <h2>6. Data Sharing</h2>
                    <p>We share personal data only where necessary and with the following categories of recipients:</p>
                    <ul>
                        <li><strong>Fly.io Inc.</strong> — Our cloud hosting provider. The Envion application and database are hosted on Fly.io's infrastructure in the London (UK) region. Fly.io processes data on our behalf as a Sub-Processor.</li>
                        <li><strong>Cloudflare Inc. (R2)</strong> — We use Cloudflare R2 for secure object storage of uploaded documents and files, such as right to work documentation and onboarding materials. Files are stored within the United Kingdom or European Economic Area. Cloudflare processes data on our behalf as a Sub-Processor.</li>
                        <li><strong>Resend Inc.</strong> — We use Resend to deliver transactional emails through the platform, such as onboarding invitations, access notifications, and reminders sent to employees when they are added to the system by their employer. Resend processes limited personal data (employee name and email address) solely for the purpose of delivering these communications on our behalf as a Sub-Processor.</li>
                        <li><strong>Google (Google Analytics)</strong> — We use Google Analytics to understand how visitors use our website. Google Analytics collects anonymised usage data. IP addresses are anonymised before transmission to Google.</li>
                        <li><strong>Microsoft (Microsoft Bookings)</strong> — We use Microsoft Bookings for scheduling demo sessions. Microsoft processes booking information (name, email, selected time) to facilitate the scheduling.</li>
                        <li><strong>Stripe</strong> — We use Stripe to process payments securely. Stripe processes payment card details and billing information. Envion does not store payment card details directly.</li>
                        <li><strong>Professional advisers</strong> — We may share data with lawyers, auditors, and insurers where necessary for professional advice or insurance purposes.</li>
                        <li><strong>Regulatory and legal authorities</strong> — We may disclose data where required by law, regulation, or court order, or to the Information Commissioner's Office.</li>
                    </ul>
                    <p>We do not sell personal data to any third party.</p>

                    <h2>7. Data Storage and Security</h2>
                    <p>All customer and platform data is stored within the United Kingdom. Our application and database are hosted on Fly.io's infrastructure in the London (UK) region. Uploaded documents and files are stored securely using Cloudflare R2 within the United Kingdom or European Economic Area. We do not transfer personal data outside the United Kingdom without prior written consent from the relevant customer and without ensuring appropriate safeguards are in place under UK GDPR.</p>
                    <p>We maintain appropriate technical and organisational security measures to protect personal data, including encryption of data in transit and at rest, role-based access controls and secure authentication, regular security monitoring and assessments, staff confidentiality obligations, and secure backup procedures.</p>

                    <h2>8. Data Retention</h2>
                    <h3>8.1 Website Visitor Data</h3>
                    <p>We retain enquiry and contact information for up to 24 months from your last interaction with us, unless you request earlier deletion. Analytics data collected through Google Analytics is retained in anonymised form.</p>

                    <h3>8.2 Customer and Platform Data</h3>
                    <p>We retain customer account data and all associated platform data (including employee personal data) for the duration of the customer's subscription. Upon cancellation, the customer's account enters a 7-day soft deletion period during which data remains accessible. At the end of the 7-day period, all customer and platform data is permanently and irreversibly deleted from our systems.</p>

                    <h3>8.3 Payment Data</h3>
                    <p>Payment transaction records are retained for as long as necessary to comply with legal, tax, and accounting obligations (typically 6 years under HMRC requirements). Envion does not store payment card details — these are held securely by Stripe.</p>

                    <h2>9. Your Rights</h2>
                    <p>Under UK GDPR, you have the following rights regarding your personal data:</p>
                    <ul>
                        <li><strong>Right of access</strong> — You can request a copy of the personal data we hold about you.</li>
                        <li><strong>Right to rectification</strong> — You can request that we correct any inaccurate or incomplete data.</li>
                        <li><strong>Right to erasure</strong> — You can request that we delete your personal data where there is no compelling reason for continued processing.</li>
                        <li><strong>Right to restriction of processing</strong> — You can request that we restrict processing of your data in certain circumstances.</li>
                        <li><strong>Right to data portability</strong> — You can request that we provide your data in a structured, commonly used, machine-readable format.</li>
                        <li><strong>Right to object</strong> — You can object to processing based on legitimate interests or for direct marketing purposes.</li>
                        <li><strong>Right to withdraw consent</strong> — Where processing is based on consent, you can withdraw that consent at any time.</li>
                    </ul>
                    <p>To exercise any of these rights, please contact us at <a href="mailto:enquiries@envion.org.uk">enquiries@envion.org.uk</a>. We will respond to your request within one month.</p>
                    <p>For employees being onboarded through the platform: If you are an employee whose data has been processed through Envion by your employer, your employer is the Data Controller. You should direct any data rights requests to your employer in the first instance. We will assist your employer in fulfilling those requests in accordance with our obligations as a Data Processor.</p>

                    <h2>10. Marketing Communications</h2>
                    <p>We will only send marketing communications to you where you have given explicit consent to receive them. You can withdraw consent and unsubscribe from marketing communications at any time by clicking the unsubscribe link in any marketing email or contacting us at <a href="mailto:enquiries@envion.org.uk">enquiries@envion.org.uk</a>.</p>

                    <h2>11. Children's Data</h2>
                    <p>Our website and services are not directed at individuals under the age of 18. We do not knowingly collect personal data from children. If we become aware that we have collected personal data from a child, we will take steps to delete that data promptly.</p>

                    <h2>12. Cookies</h2>
                    <p>Our website uses cookies to enhance your browsing experience and provide certain functionality. For full details about the cookies we use and how to manage them, please see our <Link to="/cookies">Cookies Policy</Link>.</p>

                    <h2>13. Changes to This Policy</h2>
                    <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. Where changes are material, we will notify you by updating the date at the top of this policy and, where appropriate, by email or through our website. We encourage you to review this policy periodically.</p>

                    <h2>14. Complaints</h2>
                    <p>If you are not satisfied with how we handle your personal data or our response to any request you make regarding your rights, you have the right to lodge a complaint with the Information Commissioner's Office (ICO):</p>
                    <p>Information Commissioner's Office Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a><br />Telephone: 0303 123 1113</p>
                    <p>We would appreciate the opportunity to address your concerns before you approach the ICO, so please contact us in the first instance at <a href="mailto:enquiries@envion.org.uk">enquiries@envion.org.uk</a>.</p>

                    <h2>15. Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy or our data protection practices, please contact us at:</p>
                    <p><strong>Envion Ltd</strong><br />Email: <a href="mailto:enquiries@envion.org.uk">enquiries@envion.org.uk</a></p>
                </div>
            </main>

            {/* Footer */}
            <footer className="site-footer">
                <div className="footer-content">
                    <div className="footer-brand">
                        <h2><i className="fa-solid fa-layer-group"></i> envion</h2>
                        <p className="footer-subtitle"
                            style={{ fontSize: '1.1rem', color: 'white', lineHeight: 1.5, marginBottom: '24px' }}>
                            envion is a modern workforce readiness platform. Enabling faster, smarter onboarding and preboarding
                            for today’s organisations.
                        </p>
                        <div className="footer-contact-info">
                            <p><i className="fa-solid fa-envelope"></i> <a href="mailto:enquiries@envion.org.uk"
                                style={{ color: 'white', textDecoration: 'none' }}>enquiries@envion.org.uk</a></p>
                        </div>
                    </div>

                    <div className="footer-column">
                        <h3>Solutions</h3>
                        <ul className="footer-links">
                            <li><Link to="/solutions#use-cases">Onboarding</Link></li>
                            <li><Link to="/solutions#use-cases">Preboarding</Link></li>
                        </ul>
                    </div>

                    <div className="footer-column">
                        <h3>Company</h3>
                        <ul className="footer-links">
                            <li><Link to="/about">About Us</Link></li>
                            <li><Link to="/contact">Contact</Link></li>
                        </ul>
                    </div>

                    <div className="footer-column">
                        <h3>Connect</h3>
                        <ul className="footer-social-links"
                            style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '16px', marginBottom: '24px' }}>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white' }}><i className="fa-brands fa-instagram"></i></a>
                            </li>
                            <li><a href="#" style={{ fontSize: '1.5rem', color: 'white' }}><i className="fa-brands fa-linkedin"></i></a>
                            </li>
                        </ul>
                        <div className="footer-compliance-badges"
                            style={{ color: 'white', fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.4 }}>
                            <p style={{ marginBottom: '4px' }}>GDPR Compliant</p>
                            <p>Registered with ICO</p>
                        </div>
                    </div>
                </div>

                <div className="footer-bottom">
                    <div className="footer-bottom-grid">
                        <div className="footer-legal-info">
                            <p className="copyright">&copy; 2026 envion Inc. All rights reserved.</p>
                        </div>
                        <div className="footer-legal-links">
                            <Link to="/terms">Terms & Conditions</Link>
                            <Link to="/privacy">Privacy Policy</Link>
                            <Link to="/cookies">Cookies Policy</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Privacy;
